import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSetAtom } from 'jotai';

import { useEmailApiClient } from '@/api/clients/useEmailApiClient.util.ts';
import { EmailThreadDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { returnPromise } from '@/utils/returnPromise.util.ts';
import { alreadySentDraftsAtom } from '@/views/emails/model/alreadySentDrafts.atom';
import { useEmailsHandlers } from '@/views/emails/util/useEmailsHandlers.util.ts';

type Req = {
  draftId: string;
  threadId: string;
  userId: string;
};

type Return = {
  isSendingEmailFromDraft: boolean;
  sendEmailFromDraft: (req: Req) => Promise<void>;
};

const SENT_DRAFT_REFETCH_ATTEMPTS = 4;
const SENT_DRAFT_REFETCH_DELAY_MS = 400;

export const useSendEmailFromDraft = (): Return => {
  const { emailReaderApi, emailSenderApi } = useEmailApiClient();
  const queryClient = useQueryClient();
  const setAlreadySentDrafts = useSetAtom(alreadySentDraftsAtom);
  const { refetchCurrentThreads } = useEmailsHandlers();

  const { isPending, mutateAsync } = useMutation<void, AxiosError, Req>({
    meta: {
      disableSuccessAlert: true,
    },
    mutationFn: async req => {
      await emailSenderApi.sendEmailFromDraft(req.userId, req.draftId);
      setAlreadySentDrafts(current => new Set(current).add(req.draftId));
    },
    mutationKey: [QueryKeyEnum.EMAILS.DRAFT.SEND],
    onError: async (err, req) => {
      await returnPromise(null, 3000);
      const thread = await readThread(req);
      const stillDraft = thread?.emails.some(e => e.emailId === req.draftId && e.isDraft);
      if (stillDraft !== false) throw err;
      setAlreadySentDrafts(current => new Set(current).add(req.draftId));
    },
    onSuccess: async (_, req) => {
      await refetchCurrentThreads();
      await refetchSentDraftThread(req);
      await refetchCurrentThreads();
    },
  });

  const refetchSentDraftThread = async (req: Req) => {
    for (let attempt = 0; attempt < SENT_DRAFT_REFETCH_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        await returnPromise(null, SENT_DRAFT_REFETCH_DELAY_MS);
      }

      const thread = await readThread(req);

      if (!thread) {
        return;
      }

      const draftStillVisible = thread.emails.some(
        email => email.emailId === req.draftId && email.isDraft,
      );

      if (!draftStillVisible) {
        return;
      }
    }
  };

  const readThread = async (req: Req): Promise<EmailThreadDto | null> => {
    try {
      const { data } = await emailReaderApi.readThread(req.userId, req.threadId);
      queryClient.setQueryData([QueryKeyEnum.EMAILS.THREAD, req.threadId], data);
      return data;
    } catch {
      return null;
    }
  };

  return {
    isSendingEmailFromDraft: isPending,
    sendEmailFromDraft: mutateAsync,
  };
};
